import PlusIcon from "@assets/icons/plus.svg";
import { useI18n } from "@core/i18n/i18n";
import { onboardingManager } from "@core/service-container";
import { BaseExercise } from "@domain/exercise/exercise";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import styled from "@emotion/styled";
import { AnimatedHalo } from "@ui/components/onboarding/animated-halo";
import { EXERCISE_FOOTER_HEIGHT } from "@ui/pages/program-builder/library/exercise-footer-options";
import { physioMeTabletBreakpoint, row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { motion } from "framer-motion";
import { useObservable } from "micro-observables";
import { alternativeHaloVisible, requestAlternativePanelQuickstartGuide, resetAlternativeGuide3, showAlternativeGuide3, hideAlternativeHalo } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import React, { useRef, useState } from "react";

interface AddAlternativesButtonProps {
	onClick: () => void;
	alternatives: BaseExercise[];
	shouldAnimateSuccess: boolean;
	shouldDisplayHalo?: boolean;
}

export const AddAlternativesButton: React.FC<AddAlternativesButtonProps> = ({
	onClick,
	alternatives,
	shouldAnimateSuccess,
	shouldDisplayHalo,
 
}) => {
	const { format } = useI18n();
	const [isHovering, setIsHovering] = useState(false);
	const signalHalo = useObservable(alternativeHaloVisible);
	const showHalo = signalHalo && (shouldDisplayHalo ?? true);

	const buttonRef = useRef<HTMLDivElement>(null);

	return (
		<Container
			onClick={async () => {
				if (signalHalo) {
					resetAlternativeGuide3();
					requestAlternativePanelQuickstartGuide();
					showAlternativeGuide3();
				}
				hideAlternativeHalo();
				void onboardingManager.passStepOnboarding(
					OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_01,
					true,
				);
				onClick();
			}}
			onMouseEnter={() => {
				setIsHovering(true);
			}}
			onMouseOver={() => {
				setIsHovering(true);
			}}
			onMouseLeave={() => {
				setIsHovering(false);
			}}
		>
			{shouldAnimateSuccess && alternatives.length > 0 && (
				<GreenHalo
					style={{
						width: buttonRef.current?.clientWidth,
					}}
					animate={{
						scale: [0, 1.1, 1.1],
						scaleY: [0, 1.2, 1.2],
						opacity: [0, 0.15, 0],
					}}
					transition={{
						duration: 0.6,
						delay: 0.3,
					}}
				/>
			)}
			<AddButton ref={buttonRef} hasAlreadySomeAlternatives={alternatives.length > 0} isHovering={isHovering}>
				<AnimatedHalo shouldDisplayHalo={showHalo}>
					<AddButtonContent>
						<PlusIcon />
						<p className="p-size-s-bold" style={{ fontWeight: 600 }}>
							{format("programBuilder.exerciseCard.addAlternative", {
								alternativesCount: alternatives.length,
							})}
						</p>
					</AddButtonContent>
				</AnimatedHalo>
			</AddButton>
		</Container>
	);
};

const Container = styled.div`
	height: 24px;
	padding: 0 16px;
	cursor: pointer;
`;

const resolveAddButtonColor = (hasAlreadySomeAlternatives: boolean, isHovering: boolean) => {
	if (isHovering) {
		return hasAlreadySomeAlternatives ? colors.validationDark : colors.primary;
	}
	return hasAlreadySomeAlternatives ? colors.validation : colors.grey;
};

const AddButton = styled.div<{ hasAlreadySomeAlternatives: boolean; isHovering: boolean }>`
	${row(0, "flexstart", "center")};
	width: max-content;
	color: ${({ hasAlreadySomeAlternatives, isHovering }) => resolveAddButtonColor(hasAlreadySomeAlternatives, isHovering)};
`;

const AddButtonContent = styled.div`
	${row(0, "flexstart", "center")};
	margin-right: 8px;
`;

const GreenHalo = styled(motion.div)`
	background-color: ${colors.validation};
	position: absolute;
	height: 28px;
	left: 16px;
	bottom: 14px;
	border-radius: 20px;
	pointer-events: none;
	@media ${physioMeTabletBreakpoint} {
		bottom: ${EXERCISE_FOOTER_HEIGHT + 14}px;
	}
`;
 
